import { useState, useEffect } from "react";
import PublisherForm from "../Components/Publisher/PublisherForm";
import FormHeader from "../Components/Publisher/FormHeader";
import {
  Mail,
  Building2,
  Phone,
  Users,
  CircleCheck,
  Eye,
  EyeOff,
  Edit2,
  Trash2,
  XCircle,
  UserCheck,
  Clock,
  UserX,
  X,
} from "lucide-react";
import PublishersTable from "../Components/Publisher/PublishersTable";
import { motion } from "framer-motion";
import PublisherApiService from "../Services/PublisherApiService";
import { withToastAndLoader } from "../Utils/withToastAndLoader";



export default function Publishers() {

  const [form, setForm] = useState({
    name: "",
    email: "",
    company: "",
    phone: "",
    password: "",
    status: "Pending",
  });
  const [isEditing, setIsEditing] = useState(false);
  const [editId, setEditId] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showPasswords, setShowPasswords] = useState({});

  const togglePasswordVisibility = (id) =>
    setShowPasswords((prev) => ({ ...prev, [id]: !prev[id] }));

  const openModal = () => setModalOpen(true);
  const closeModal = () => {
    resetForm();
    setModalOpen(false);
  };

  const resetForm = () => {
    setForm({ name: "", email: "", status: "Pending", password: "" });
    setIsEditing(false);
    setEditId(null);
  };

  useEffect(() => {
    loadPublishers();
  }, []);
  const loadPublishers = async () => {
    await withToastAndLoader(() => PublisherApiService.getAllPublishers(), {
      setLoading,
      preset: "fetch",
    }).then((result) => setData([...(result || [])].sort((a, b) => b.id - a.id)));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    await withToastAndLoader(async () => {
      if (isEditing) {
        await PublisherApiService.updatePublisher(editId, form);
      } else {
        await PublisherApiService.createPublisher(form);
        PublisherApiService.sendDashboardCredentials({
          customerName: form.name,
          email: form.email,
          username: form.email,
          password: form.password,
        }).catch((err) => console.error("❌ Failed to send dashboard credentials:", err));
      }
      await loadPublishers();
      closeModal();
    }, {
      setLoading,
      preset: isEditing ? "update" : "add",
    });
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this publisher?")) return;

    await withToastAndLoader(async () => {
      await PublisherApiService.deletePublisher(id);
      await loadPublishers();
    }, {
      setLoading,
      preset: "delete",
    });
  };


  const handleEdit = (row) => {
    setForm(row);
    setEditId(row.id);
    setIsEditing(true);
    openModal();
  };



  const stats = [
    {
      name: "Total Publishers",
      value: data.length,
      icon: Users,
      color: "text-indigo-600",
      bgColor: "bg-indigo-100",
    },
    {
      name: "Active",
      value: data.filter((p) => p.status === "Active").length,
      icon: UserCheck,
      color: "text-green-600",
      bgColor: "bg-green-100",
    },
    {
      name: "Pending",
      value: data.filter((p) => p.status === "Pending").length,
      icon: Clock,
      color: "text-yellow-600",
      bgColor: "bg-yellow-100",
    },
    {
      name: "Suspended",
      value: data.filter((p) => p.status === "Suspended").length,
      icon: UserX,
      color: "text-red-600",
      bgColor: "bg-red-100",
    },
  ];

  const columns = [
    {
      name: "Publisher",
      selector: row => row.name,
      cell: row => (
        <div className="flex items-center gap-3">
          <div className="flex-shrink-0 w-9 h-9 rounded-full bg-gradient-to-br from-indigo-500 to-purple-500 flex items-center justify-center text-white text-sm font-semibold">
            {row.name?.charAt(0)?.toUpperCase() || "?"}
          </div>
          <div>
            <div className="font-medium text-gray-800 text-sm">{row.name}</div>
            <div className="text-xs text-gray-500 flex items-center gap-1">
              <Mail className="w-3 h-3" />
              {row.email}
            </div>
          </div>
        </div>
      ),
      sortable: true,
      minWidth: "220px",
    },
    {
      name: "Company",
      selector: row => row.company,
      cell: row => (
        <div className="flex items-center gap-2 text-gray-700">
          <Building2 className="w-4 h-4 text-emerald-500" />
          <span className="text-sm">{row.company || <span className="text-gray-400 italic">—</span>}</span>
        </div>
      ),
      minWidth: "180px",
    },
    {
      name: "Phone",
      selector: row => row.phone,
      cell: row => (
        <div className="flex items-center gap-2 text-gray-700">
          <Phone className="w-4 h-4 text-yellow-500" />
          <span className="text-sm">{row.phone || <span className="text-gray-400 italic">—</span>}</span>
        </div>
      ),
      minWidth: "160px",
    },
    {
      name: "Password",
      selector: row => row.password,
      cell: row => (
        <div className="flex items-center gap-2">
          <span className="text-sm font-mono text-gray-700">
            {showPasswords[row.id] ? row.password : "••••••••"}
          </span>
          <button
            onClick={() => togglePasswordVisibility(row.id)}
            className="p-1 text-gray-400 hover:text-gray-600 transition-colors"
          >
            {showPasswords[row.id] ? (
              <EyeOff className="w-4 h-4" />
            ) : (
              <Eye className="w-4 h-4" />
            )}
          </button>
        </div>
      ),
      minWidth: "170px",
    },
    {
      name: "Status",
      selector: row => row.status,
      cell: row => {
        const statusStyles = {
          Active: "bg-green-100 text-green-700",
          Suspended: "bg-red-100 text-red-600",
          Pending: "bg-yellow-100 text-yellow-700",
        };
        const statusIcons = {
          Active: <CircleCheck className="w-3.5 h-3.5" />,
          Suspended: <XCircle className="w-3.5 h-3.5" />,
          Pending: <Clock className="w-3.5 h-3.5" />,
        };
        return (
          <span className={`inline-flex items-center gap-1.5 text-xs font-semibold px-2.5 py-1 rounded-full ${statusStyles[row.status] || "bg-gray-100 text-gray-600"}`}>
            {statusIcons[row.status]}
            {row.status}
          </span>
        );
      },
      sortable: true,
      minWidth: "140px",
    },
    {
      name: "Actions",
      cell: (row) => (
        <div className="flex items-center gap-1">
          <button
            onClick={() => handleEdit(row)}
            className="p-2 text-indigo-600 hover:bg-indigo-50 rounded-lg transition-colors"
            title="Edit"
          >
            <Edit2 className="w-4 h-4" />
          </button>
          <button
            onClick={() => handleDelete(row.id)}
            className="p-2 text-red-500 hover:bg-red-50 rounded-lg transition-colors"
            title="Delete"
          >
            <Trash2 className="w-4 h-4" />
          </button>
        </div>
      ),
      minWidth: "120px",
    },
  ];
  return (
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4 }}
      className="p-4 md:p-6 space-y-6 max-w-screen"
    >
      {loading && (
        <div className="flex justify-center items-center py-6">
          <div className="relative w-12 h-12">
            <div className="absolute inset-0 rounded-full border-4 border-indigo-300 border-t-transparent animate-spin" />
            <div className="absolute inset-2 rounded-full bg-indigo-200 blur-xl opacity-50" />
            <div className="absolute inset-[6px] rounded-full bg-indigo-500 animate-ping" />
            <div className="absolute inset-[10px] rounded-full bg-white shadow-inner" />
          </div>
        </div>
      )}

      {/* Stats Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {stats.map((stat, index) => (
          <motion.div
            key={stat.name}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3, delay: index * 0.07 }}
            className="relative bg-white/80 backdrop-blur-sm rounded-xl border border-gray-100 shadow-sm p-4 hover:shadow-md transition-all"
          >
            <div className="flex justify-between items-start">
              <div>
                <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">{stat.name}</p>
                <h2 className="mt-1 text-3xl font-bold text-gray-800">{stat.value}</h2>
              </div>
              <div className={`p-2.5 rounded-xl ${stat.bgColor} ${stat.color}`}>
                <stat.icon className="w-5 h-5" />
              </div>
            </div>
          </motion.div>
        ))}
      </div>

      {/* Table with Add button and heading */}
      <PublishersTable data={data || []} columns={columns} openModal={openModal} />

      {/* Modal */}
      {modalOpen && (
        <div className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm flex items-center justify-center p-4 sm:p-6">
          <motion.div
            initial={{ opacity: 0, y: 20, scale: 0.97 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20 }}
            transition={{ type: "spring", damping: 25, stiffness: 400 }}
            className="relative bg-white rounded-2xl shadow-2xl w-full max-w-2xl overflow-hidden border border-gray-100"
          >
            {/* Decorative gradient blobs */}
            <div className="absolute -right-20 -top-20 w-64 h-64 rounded-full bg-indigo-100/30 filter blur-3xl pointer-events-none" />
            <div className="absolute -left-10 -bottom-10 w-48 h-48 rounded-full bg-purple-100/20 filter blur-2xl pointer-events-none" />

            <div className="relative z-10">
              {/* Modal header */}
              <div className="flex items-center justify-between p-6 border-b border-gray-100 bg-gradient-to-r from-white to-gray-50">
                <FormHeader isEditing={isEditing} />
                <button
                  onClick={closeModal}
                  className="p-2 rounded-full hover:bg-gray-100 text-gray-400 hover:text-gray-600 transition-all"
                  aria-label="Close modal"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              {/* Form */}
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.2 }}
              >
                <PublisherForm
                  form={form}
                  isEditing={isEditing}
                  onChange={setForm}
                  onSubmit={handleSubmit}
                  onCancel={closeModal}
                />
              </motion.div>
            </div>
          </motion.div>
        </div>
      )}
    </motion.div>
  );
}
